import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { compact, isEmpty, isEqual } from 'lodash';
import { ValuesType } from 'utility-types';
import { Button, DateFilters as EbscoDateFilters } from '@ebsco-ui/ebsco-ui';

import { facet, useUrlManager } from '@app/components';
import {
  DATE_FORMAT,
  DateRange,
  formatDateRange,
  FormattedDateRange,
  getDateRangeOptions,
  getInitialDateRange,
  removeEmptyKeys,
  validateDateRange,
} from '@app/utils';
import { dateFiltersMessages, sharedMessages } from '@app/translations';
import { quickFilterType, SetState, ShapeWithClassName } from '@app/constants';

interface DateFiltersProps {
  ariaLabel: string;
  hasApplyButton?: boolean;
  onDateRangeChange: SetState<DateRange>;
  selectedDateRange: DateRange;
}

export const DateFilters: FC<ShapeWithClassName<DateFiltersProps>> = ({
  ariaLabel,
  hasApplyButton = true,
  selectedDateRange,
  onDateRangeChange,
}) => {
  const initialDateRange = useMemo(getInitialDateRange, []);
  const { $t } = useIntl();
  const [selectedFilterType, setSelectedFilterType] = useState<
    keyof typeof quickFilterType
  >(quickFilterType.allTime);
  const [dateRange, setDateRange] = useState<FormattedDateRange>(
    getInitialDateRange()
  );
  const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState(false);
  const { getSearchStateFromUrl } = useUrlManager();
  const isInitialSelectionSet = useRef(false);

  const quickFilters = useMemo(
    () =>
      Object.values(quickFilterType).map(filterType => ({
        label: $t(dateFiltersMessages[filterType]),
        value: filterType,
      })),
    [$t]
  );
  const {
    options: dateRangeOptions,
    invertedOptions: invertedDateRangeOptions,
  } = useMemo(getDateRangeOptions, []);

  const dateFiltersI18n = {
    groupTitle: ariaLabel,
    minDateLabel: $t(sharedMessages.from),
    maxDateLabel: $t(sharedMessages.to),
  };
  const isCustomRangeSelected = selectedFilterType === quickFilterType.custom;

  useEffect(() => {
    const isSelectionSyncedWithUrl = isEqual(
      getSearchStateFromUrl().publicationYear ?? {},
      removeEmptyKeys(selectedDateRange)
    );
    const isAllTime = isEmpty(getSearchStateFromUrl().publicationYear ?? {});

    if (
      isSelectionSyncedWithUrl &&
      (!isInitialSelectionSet.current || isAllTime)
    ) {
      const determineFilterType = (): keyof typeof quickFilterType => {
        if (isEmpty(selectedDateRange)) {
          return quickFilterType.allTime;
        }

        return (
          invertedDateRangeOptions[selectedDateRange.minDate ?? ''] ??
          quickFilterType.custom
        );
      };

      const filterType = determineFilterType();

      setSelectedFilterType(filterType);
      isInitialSelectionSet.current = true;

      if (filterType === quickFilterType.custom) {
        setDateRange(formatDateRange(selectedDateRange, $t));
      }
    }
  }, [$t, getSearchStateFromUrl, invertedDateRangeOptions, selectedDateRange]);

  const changeFilterType = (filterType: ValuesType<typeof quickFilterType>) => {
    const dateFilterValue = dateRangeOptions[filterType];

    onDateRangeChange(dateFilterValue ? { minDate: dateFilterValue } : {});
    setDateRange(initialDateRange);
    setIsApplyButtonDisabled(false);
  };

  const changeDateRange = (customDateRange: DateRange) => {
    const { hasDateError } = validateDateRange(customDateRange);
    const formattedDateRange = formatDateRange(customDateRange, $t);

    setSelectedFilterType(quickFilterType.custom);
    setDateRange(formatDateRange(customDateRange, $t));
    setIsApplyButtonDisabled(hasDateError);

    if (!hasApplyButton && !hasDateError) {
      onDateRangeChange({
        minDate: formattedDateRange.minDate?.value,
        maxDate: formattedDateRange.maxDate?.value,
      });
    }
  };

  const handleDateFiltersChange = ({ selectedFilter, customRange }) => {
    setSelectedFilterType(selectedFilter);

    if (selectedFilter === quickFilterType.custom) {
      changeDateRange(customRange);
    } else if (selectedFilter !== selectedFilterType) {
      changeFilterType(selectedFilter);
    }
  };

  const handleApplyButtonClick = () => {
    onDateRangeChange({
      minDate: dateRange.minDate?.value,
      maxDate: dateRange.maxDate?.value,
    });
  };

  return (
    <>
      {compact([
        <EbscoDateFilters
          className="publication-filters"
          key={facet.publication_year}
          i18n={dateFiltersI18n}
          dateRange={dateRange}
          isSharedError={dateRange.isSharedError}
          quickFilters={quickFilters}
          selectedFilter={selectedFilterType}
          showDateRange={isCustomRangeSelected}
          placeholder={DATE_FORMAT.YYYY}
          onChange={handleDateFiltersChange}
        />,
        hasApplyButton && isCustomRangeSelected && (
          <Button
            key="applyButton"
            disabled={isApplyButtonDisabled}
            onClick={handleApplyButtonClick}
          >
            {$t(sharedMessages.apply)}
          </Button>
        ),
      ])}
    </>
  );
};
